import React from 'react';
import { Outlet } from 'react-router-dom';
import { SidebarProvider, useSidebar } from '@contexts/SidebarContext';
import { ThemeProvider } from '@contexts/ThemeContext';
import { AsideProvider, useAsideContext } from '@contexts/AsideContext';
import { AppSidebar } from './AppSidebar';
import { AppHeader } from './AppHeader';
import { Backdrop } from './Backdrop';
import { Aside } from '@common/components/core';
import { clsx } from 'clsx';

const GlobalAside: React.FC = () => {
  const { isOpen, content, config, closeAside } = useAsideContext();
  return (
    <Aside
      isOpen={isOpen}
      onClose={closeAside}
      title={config.title}
      description={config.description}
      width={config.width}
      actions={config.actions}
    >
      {content}
    </Aside>
  );
};

const LayoutContent: React.FC = () => {
  const { isOpen: isAsideOpen } = useAsideContext();
  const { isExpanded, isHovered } = useSidebar();
  const shouldExpand = (isExpanded || isHovered) && !isAsideOpen;

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50 dark:bg-gray-900">
      <AppSidebar />
      <Backdrop />
      <GlobalAside />

      {/* Main content area — offset collapses to 0 while an Aside drawer is open */}
      <div
        className={clsx(
          'flex-1 flex flex-col overflow-hidden transition-all duration-300',
          !isAsideOpen && (shouldExpand ? 'md:ml-64' : 'md:ml-20')
        )}
      >
        <AppHeader />

        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export const AppLayout: React.FC = () => {
  return (
    <ThemeProvider>
      <SidebarProvider>
        <AsideProvider>
          <LayoutContent />
        </AsideProvider>
      </SidebarProvider>
    </ThemeProvider>
  );
};
